import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCheck, Loader2, PanelLeftClose, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ListSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '../store/notificationStore';
import { useServerNow } from '../hooks/useServerNow';
import { compareDesc } from '../lib/notificationMerge';
import type { Notification } from '../types/notification.types';
import { NotificationRow } from './NotificationRow';
import { DesktopPushToggle } from './DesktopPushToggle';
import { NotificationSoundToggle } from './NotificationSoundToggle';

/**
 * THE notification surface (M6 · 86d44z2b7). Was a Radix Popover hanging off a sidebar nav item;
 * is now an in-flow 360px panel hanging off the rail's bell.
 *
 * <b>What M6 changed and what it deliberately did not.</b> The CONTAINER is new — chrome, header,
 * close control, surface token. Everything inside is carried across unchanged, including
 * `NotificationRow`, which this file does not import differently and does not touch. That single
 * choice is what keeps a feature hardened by an 18-defect campaign out of this ticket's risk
 * surface: read-on-click, the flat chronological merge, the density pass and `--primary-ink` cannot
 * regress here, because the code that implements them was not edited.
 *
 * ⚠️ <b>Radix used to provide three behaviours for free and an `<aside>` inherits NONE of them.</b>
 * Escape-to-dismiss and focus-return were never implemented in the popover — they were Radix's. They
 * are now explicit: AppLayout owns the Escape listener and the bell ref, and returns focus there.
 *
 * ⛔ <b>Outside-click does NOT dismiss, deliberately.</b> A push panel is not an overlay: it moves
 * the page over rather than covering it, so clicking the content beside it is ordinary work, not a
 * dismissal gesture. Closing on it would make the inbox unusable the moment you tried to act on
 * anything it told you about. The bell and the panel's own control are the two ways out.
 *
 * ⚠️ <b>Radix portals escape this subtree.</b> The row's ⋯ menu and its tooltips attach to
 * `document.body`, outside `[data-surface="sidebar"]`, so in Industrial they render with
 * light-canvas tokens: a white menu over the charcoal panel. Legible, and measured as such — but
 * not charcoal-themed. Fixing it means giving those portals the sidebar tokens at their own call
 * sites inside `NotificationRow`, which this ticket is explicitly not allowed to edit.
 */
export function NotificationPanel({ onRequestClose }: { onRequestClose: () => void }) {
  return (
    <aside
      aria-label="Notifications"
      // Industrial re-bases the generic tokens for this subtree — see globals.css.
      data-surface="sidebar"
      className="shell-panel-enter flex w-panel min-h-0 flex-shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      <NotificationPanelBody onRequestClose={onRequestClose} />
    </aside>
  );
}

/** The `All | Needs action | Unread` pill filter. ⚠️ PRESENTATION ONLY — see the note where it is used. */
type PanelFilter = 'all' | 'needs' | 'unread';

/**
 * ⛔ THE PANEL DOES NOT PAGE. Exactly one request per open transition, and `nextCursor` is ignored.
 *
 * The `/notifications` page owns paging, with the house "Show more" button. House precedent for
 * fetching unpaged in a glance surface: `QuotationMasterList.tsx:143-146`. A panel is a glance
 * surface; paging belongs on the page.
 */
function NotificationPanelBody({ onRequestClose }: { onRequestClose: () => void }) {
  const items = useNotificationStore((s) => s.items);
  const pinned = useNotificationStore((s) => s.pinned);
  const pinnedTruncated = useNotificationStore((s) => s.pinnedTruncated);
  const isLoading = useNotificationStore((s) => s.isLoading);
  const error = useNotificationStore((s) => s.error);
  const needsCount = useNotificationStore((s) => s.needsCount);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  const nowMs = useServerNow();
  const [filter, setFilter] = useState<PanelFilter>('all');

  // The pill's visual reset. The popover got this free by unmounting on close; an in-flow panel
  // that is conditionally rendered gets it the same way — but state it, because if the panel is
  // ever kept mounted the filter would silently persist across opens.
  useEffect(() => setFilter('all'), []);

  // ⚠️ Derived IN-COMPONENT from the directly-subscribed arrays. ⛔ Never behind a store getter —
  // that is the 86d3d1z40 shape, and the list would freeze at its first computed value.
  //
  // ⚠️ `All` merges `pinned` INTO `items` rather than reading `items` alone. The two are separate
  // server collections: `pinned` is the whole needs-action set (capped at 50), `items` is the loaded
  // keyset window. A needs-action row older than that window exists in `pinned` only — reading
  // `items` alone would hide from `All` a row the red badge is actively counting.
  const byId = new Map<number, Notification>();
  for (const n of pinned) byId.set(n.id, n);
  for (const n of items) if (!byId.has(n.id)) byId.set(n.id, n);
  const allRows = [...byId.values()].sort(compareDesc);

  // ⚠️ `unread` MEANS UNREAD. A read-but-unresolved failure is legal state (and read-on-click made
  // it common), so it WILL be absent here — it is one click away under its own destructive-tinted
  // pill, which is the affordance that replaced the old pinned band.
  const rows =
    filter === 'needs'
      ? pinned
      : filter === 'unread'
        ? allRows.filter((n) => n.unread)
        : allRows;

  if (isLoading && items.length === 0 && pinned.length === 0) {
    return (
      <>
        <PanelHeader needsCount={needsCount} unreadCount={unreadCount} onRequestClose={onRequestClose} />
        {/* A panel CAN cold-load — the redesign dropped this and gate #2 requires it. */}
        <div className="p-3">
          <ListSkeleton rows={4} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PanelHeader needsCount={needsCount} unreadCount={unreadCount} onRequestClose={onRequestClose} />
        {/* Inline, with a retry. ⛔ Never a toast: the surface that failed is the surface that says so. */}
        <div className="p-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => void useNotificationStore.getState().fetch({ includePinned: true })}
          >
            Try again
          </Button>
        </div>
      </>
    );
  }

  const isEmpty = items.length === 0 && pinned.length === 0;

  return (
    <>
      <PanelHeader needsCount={needsCount} unreadCount={unreadCount} onRequestClose={onRequestClose} />

      {!isEmpty && (
        <FilterPills
          value={filter}
          onChange={setFilter}
          needsCount={needsCount}
          unreadCount={unreadCount}
        />
      )}

      {isEmpty ? (
        <EmptyState
          icon={PartyPopper}
          title="All caught up"
          description="Postings you submit will show up here."
          className="py-10"
        />
      ) : (
        /* `overscroll-contain`: without it, scrolling past the end of the list chains to the page
           beside the panel, so a flick to reach the oldest row scrolls the record instead. */
        <div className="flex-1 space-y-2 overflow-y-auto overscroll-contain p-3">
          {rows.map((n) => (
            <NotificationRow key={n.id} notification={n} nowMs={nowMs} onNavigate={onRequestClose} />
          ))}

          {/* The needs-action set is capped server-side at 50 (`NotificationRepository.PinnedCap`).
              Naming the remainder is what stops the red badge and this list silently disagreeing.
              ⚠️ Shown on `all` TOO, not just `needs`: the panel opens on `all`, and `all` is built
              from the same capped set, so gating it on `needs` would put the only disclosure of the
              cap behind a click the affected user has no reason to make. */}
          {filter !== 'unread' && pinnedTruncated && (
            <Link
              to="/notifications?tab=needs"
              onClick={onRequestClose}
              className="block pt-1 text-xs font-medium text-destructive underline-offset-2 hover:underline"
            >
              +{Math.max(needsCount - pinned.length, 1)} more — view all
            </Link>
          )}

          {rows.length === 0 && (
            <p className="py-6 text-center text-xs text-muted-foreground">
              {filter === 'needs'
                ? 'Nothing needs your attention.'
                : filter === 'unread'
                  ? 'Nothing unread.'
                  : 'Nothing else to show.'}
            </p>
          )}
        </div>
      )}

      <PanelFooter onNavigate={onRequestClose} />
    </>
  );
}

/**
 * ⛔ `Mark all read` IS WIRED HERE, NOT IMPLEMENTED HERE. The store's version posts in all-mode
 * (`ids: null`) — the one mutation that must reach rows this client has never loaded, so no
 * optimistic patch could ever be complete. The server's predicate excludes needs-action rows, so
 * clearing the blue count can never discharge a red one.
 */
function PanelHeader({
  needsCount,
  unreadCount,
  onRequestClose,
}: {
  needsCount: number;
  unreadCount: number;
  onRequestClose: () => void;
}) {
  const markAllRead = useNotificationStore((s) => s.markAllRead);
  const [busy, setBusy] = useState(false);

  const summary =
    needsCount === 0 && unreadCount === 0
      ? 'Nothing new'
      : [
          needsCount > 0 ? `${needsCount} need${needsCount === 1 ? 's' : ''} action` : null,
          unreadCount > 0 ? `${unreadCount} unread` : null,
        ]
          .filter(Boolean)
          .join(' · ');

  return (
    <div className="flex flex-shrink-0 items-start justify-between gap-2 border-b border-sidebar-border px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold">Notifications</p>
        <p className="truncate text-xs text-muted-foreground">{summary}</p>
      </div>

      <div className="flex flex-shrink-0 items-center gap-1">
        {unreadCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Icon-only (owner, 2026-08-27): the label cost ~90px of a 264px header beside a
                  title that already says "Notifications", and the checks read as "clear these".
                  Icon-only means it MUST carry a Tooltip and a matching `aria-label` — UI gate §5 —
                  and the tooltip now has to name the ACTION, not just qualify it, because the button
                  no longer says what it does. The spinner is the sanctioned exception to
                  "skeletons, not spinners": a tiny inline button-busy state. */}
              <Button
                size="icon"
                variant="ghost"
                disabled={busy}
                aria-label="Mark all as read"
                className="h-7 w-7"
                onClick={() => {
                  setBusy(true);
                  void markAllRead()
                    .catch(() => toast.error('Could not mark everything as read'))
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Mark all as read — anything needing action stays flagged</TooltipContent>
          </Tooltip>
        )}

        {/* The panel's OWN close control. Per the shell state table this is the one path that
            force-opens the module panel behind it — closing via the bell leaves it shut. Icon-only,
            so it carries a Tooltip and a matching aria-label (UI gate §5). */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onRequestClose}
              aria-label="Close notifications"
              className="h-6 w-6 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Close notifications</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

/**
 * ⚠️ PRESENTATION ONLY — ⛔ NOT A SERVER PARAMETER.
 *
 * These pills re-slice the rows already in hand: no request, no cursor, no store write. The four
 * real tabs (with server counts and their own keyset windows) live on the `/notifications` page.
 * Wiring these to `setTab` would reset the window and refire a fetch on what the user reads as a
 * local filter.
 *
 * ⚠️ `Needs action` CARRIES SEVERITY — tinted when ACTIVE, and destructive-toned whenever the count
 * is non-zero, so an unopened filter still says "there is work here". This is the only place a
 * filter control is allowed a status colour.
 */
function FilterPills({
  value,
  onChange,
  needsCount,
  unreadCount,
}: {
  value: PanelFilter;
  onChange: (next: PanelFilter) => void;
  needsCount: number;
  unreadCount: number;
}) {
  const labels: Record<PanelFilter, string> = {
    all: 'All',
    needs: `Needs action${needsCount > 0 ? ` (${needsCount})` : ''}`,
    unread: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}`,
  };

  return (
    <div
      role="group"
      aria-label="Filter notifications"
      className="flex flex-shrink-0 gap-1 border-b border-sidebar-border px-3 py-2"
    >
      {(['all', 'needs', 'unread'] as const).map((option) => {
        const active = value === option;
        const isNeeds = option === 'needs';

        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={active}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              active && isNeeds && 'bg-destructive/12 text-destructive',
              active && !isNeeds && 'bg-accent text-accent-foreground',
              !active && isNeeds && needsCount > 0 && 'text-destructive hover:bg-destructive/5',
              !active && !(isNeeds && needsCount > 0) && 'text-muted-foreground hover:bg-accent/50',
            )}
          >
            {labels[option]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The push control and the way through to the full page.
 *
 * ⚠️ `DesktopPushToggle` is reused AS A COMPONENT, not re-implemented. It renders FOUR states, two
 * of which need a hint line and one of which replaces the switch entirely — copying its markup into
 * a "promoted header" version with room for two would drop the other two silently.
 */
function PanelFooter({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-sidebar-border px-3 py-2">
      <DesktopPushToggle />
      {/* The two notification-delivery controls sit together: one decides whether the browser tells
          you when the tab is closed, the other whether this tab makes a sound. */}
      <NotificationSoundToggle />
      <Link
        to="/notifications"
        onClick={onNavigate}
        className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-foreground underline-offset-2 hover:underline"
      >
        See all
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
      </Link>
    </div>
  );
}
